// @ts-strict-ignore
import type { BaseComponent, ComponentType } from "types/contentful";

import type { TextObjectProps } from "./textObject";
import { contentfulTextObjectToText } from "./textObject";

import type { StylesObjectProps } from "./stylesObject";
import { contentfulStyleObjectsToStyles } from "./stylesObject";

import type { ImageObjectProps } from "./imageObject";
import { contentfulImageObjectToImage } from "./imageObject";
import type { Entry } from "contentful";

import type { CTAObjectProps } from "./ctaObject";
import { contentfulCTAObjectToCTA } from "./ctaObject";

interface ComponentTypeProps {
  type: string;
  template: string;
}
export interface BaseComponentProps {
  id: string;
  componentType?: ComponentTypeProps;
  theme: "dark" | "light";
  text: TextObjectProps[];
  styles: StylesObjectProps;
  cta: CTAObjectProps[];
  groupedContent: BaseComponentProps[];
  backgroundImage: ImageObjectProps;
  images: ImageObjectProps[];
  json: Record<string, unknown>;
}

const contenfulBaseComponentDefaults: BaseComponentProps = {
  id: "",
  componentType: {
    type: "",
    template: ""
  },
  theme: null,
  text: [],
  cta: [],
  styles: {},
  groupedContent: null,
  backgroundImage: null,
  images: [],
  json: {}
};

export const contentfulBaseComponent = (
  props: BaseComponent,
  userCountry?: string
): BaseComponentProps => ({
  id: props.sys?.id,
  componentType:
    contentfulComponentTypeToComponentType(props?.fields?.componentType) ||
    contenfulBaseComponentDefaults.componentType,
  theme: props?.fields?.theme || contenfulBaseComponentDefaults.theme,
  text:
    props?.fields?.text?.map((text) =>
      contentfulTextObjectToText(text, props.userCountry || userCountry)
    ) || contenfulBaseComponentDefaults.text,
  // make sure to pass country to child base components as well when recursively calling it
  groupedContent:
    props?.fields?.groupedContent?.map((component) =>
      contentfulBaseComponent(component, props.userCountry || userCountry)
    ) || contenfulBaseComponentDefaults.groupedContent,
  backgroundImage:
    contentfulImageObjectToImage(props?.fields?.backgroundImage) ||
    contenfulBaseComponentDefaults.backgroundImage,
  images:
    props?.fields?.images?.map((image) =>
      contentfulImageObjectToImage(image)
    ) || contenfulBaseComponentDefaults.images,
  cta:
    props?.fields?.cta?.map((image) => contentfulCTAObjectToCTA(image)) ||
    contenfulBaseComponentDefaults.cta,
  styles:
    contentfulStyleObjectsToStyles(
      props?.fields?.mobileStyles,
      props?.fields?.tabletStyles,
      props?.fields?.desktopStyles
    ) || contenfulBaseComponentDefaults.styles,

  json: props?.fields?.jsonObject || contenfulBaseComponentDefaults.json
});

const contentfulComponentTypeToComponentType = (
  componentType: ComponentType
): ComponentTypeProps => ({
  type: componentType?.fields?.componentType,
  template: componentType?.fields?.template
});

// create a type guard to check if an item is an Contenful Entry
export const isContentfulEntry = (item: unknown): item is Entry<any> => {
  return (item as Entry<any>).sys !== undefined;
};
